import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Download, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { StatusPill } from "./ui";

const APP_STATUSES = ["new", "screening", "interview", "offer", "hired", "rejected"];

const ApplicationsTab = () => {
  const [apps, setApps] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/applications").then((r) => setApps(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admin/applications/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const downloadResume = async (app) => {
    try {
      const res = await api.get(`/admin/applications/${app.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = app.resume_filename || "resume";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-tab-applications">
      <h2 className="font-display text-xl font-bold tracking-tight">Job Applications</h2>
      <p className="mt-1 text-sm text-tark-dim">Candidate applications and resumes — kept separate from client leads.</p>
      <div className="mt-6 space-y-3">
        {apps.map((app) => (
          <div key={app.id} className="rounded-2xl border border-tark-line bg-tark-card" data-testid={`application-row-${app.id}`}>
            <button className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 p-5 text-left" onClick={() => setExpanded(expanded === app.id ? null : app.id)}>
              <span className="font-display text-sm font-bold text-tark-ink">{app.full_name}</span>
              <span className="font-mono text-xs text-tark-mut">{app.email}</span>
              <span className="rounded border border-tark-blue/40 bg-tark-blue/10 px-2 py-0.5 font-mono text-[10px] text-tark-cyan">{app.position_applied_for}</span>
              <span className="ml-auto flex items-center gap-3">
                <StatusPill status={app.status} />
                <ChevronDown className={`h-4 w-4 text-tark-dim transition-transform ${expanded === app.id ? "rotate-180" : ""}`} />
              </span>
            </button>
            {expanded === app.id && (
              <div className="border-t border-tark-line p-5 text-sm">
                <div className="grid gap-x-8 gap-y-2 font-mono text-xs text-tark-mut sm:grid-cols-2 lg:grid-cols-3">
                  <span>Phone: {app.phone || "—"}</span>
                  <span>Location: {app.current_location || "—"}</span>
                  <span>Total Exp: {app.total_experience || "—"}</span>
                  <span>Relevant Exp: {app.relevant_experience || "—"}</span>
                  <span>Company: {app.current_company || "—"}</span>
                  <span>Notice: {app.notice_period || "—"}</span>
                  <span>Current CTC: {app.current_ctc || "—"}</span>
                  <span>Expected CTC: {app.expected_ctc || "—"}</span>
                  {app.linkedin_profile && <a href={app.linkedin_profile} target="_blank" rel="noreferrer" className="text-tark-cyan hover:underline">LinkedIn</a>}
                  {app.portfolio_github && <a href={app.portfolio_github} target="_blank" rel="noreferrer" className="text-tark-cyan hover:underline">Portfolio / GitHub</a>}
                </div>
                {app.cover_note && <p className="mt-4 leading-relaxed text-tark-mut">{app.cover_note}</p>}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <select value={app.status} onChange={(e) => setStatus(app.id, e.target.value)}
                          className="rounded-lg border border-tark-line bg-tark-bg px-3 py-2 font-mono text-xs text-tark-ink"
                          data-testid={`application-status-${app.id}`}>
                    {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {app.resume_path ? (
                    <button onClick={() => downloadResume(app)} data-testid={`resume-download-${app.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-tark-line px-3 py-2 font-mono text-xs text-tark-cyan hover:border-tark-cyan/50">
                      <Download className="h-3.5 w-3.5" /> {app.resume_filename || "Resume"}
                    </button>
                  ) : (
                    <span className="font-mono text-xs text-tark-dim">No resume attached</span>
                  )}
                  <button onClick={() => remove(app.id)} className="text-red-400/70 hover:text-red-400" data-testid={`application-delete-${app.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {apps.length === 0 && <p className="rounded-2xl border border-tark-line bg-tark-card p-10 text-center font-mono text-xs text-tark-dim">No applications yet.</p>}
      </div>
    </div>
  );
};

export default ApplicationsTab;
